import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserX } from "lucide-react";
import {
  NETWORK_ROUTES,
  parseReferralCode,
  resolveReferralResponseSchema,
} from "@nh/shared";
import { apiFetch } from "@/shared/api/client";
import { Button, PulseLoader } from "@/shared/ui/core";
import { storeReferral } from "./referral.storage";

/**
 * ═══════════════════════════════════════════════════════════════════
 * La puerta del enlace de invitación — `/register/CODE` (la estructura
 * del sistema viejo, la que generan los enlaces nuevos) y `/r/CODE`
 * (los enlaces ya repartidos siguen entrando). Misma pantalla.
 *
 * Tres capas antes de que el código toque el registro:
 *
 *   1. FORMA: `parseReferralCode` (alfabeto sin ambiguos + carácter de
 *      control). Una letra mal copiada se detecta AQUÍ, con un mensaje
 *      exacto, sin tocar la red.
 *   2. REFERENCIA: el resolvedor público confirma que hay una persona
 *      real detrás y responde su nombre de pila — lo justo para
 *      reconocerla, nada para censarla.
 *   3. El servidor VUELVE a validar todo al registrar: esta pantalla
 *      solo acomoda el camino, no es la garantía.
 *
 * Un código que no pasa NO llega al registro: es el arreglo del
 * patrocinador fantasma de producción ($779.80 colgando de un uid con
 * una letra cambiada, guardado sin comprobar).
 * ═══════════════════════════════════════════════════════════════════
 */
export function ReferralLandingPage() {
  const { t } = useTranslation();
  const { code: rawCode } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function resolver() {
      const parsed = parseReferralCode(rawCode);
      if (!parsed.ok) {
        setError(parsed.message);
        return;
      }

      try {
        const data = await apiFetch(
          NETWORK_ROUTES.resolveCode(parsed.code),
          resolveReferralResponseSchema,
          { baseUrl: "" },
        );
        if (cancelled) return;
        storeReferral({ code: parsed.code, displayName: data.sponsor.displayName });
        navigate("/register", { replace: true });
      } catch {
        if (!cancelled) {
          setError(t("Ese código no corresponde a nadie. Revísalo con quien te invitó."));
        }
      }
    }

    void resolver();
    return () => {
      cancelled = true;
    };
  }, [rawCode, navigate, t]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-ds-cream px-6">
      {error ? (
        <div className="w-full max-w-sm rounded-3xl border border-ds-border bg-white/80 p-8 text-center shadow-ds-float backdrop-blur-xl">
          <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-ds-mist text-ds-ink-3">
            <UserX className="h-5 w-5" />
          </span>
          <h1 className="mt-4 text-[17px] font-semibold text-ds-ink">
            {t("Ese enlace no funciona")}
          </h1>
          <p className="mt-2 text-[13px] leading-relaxed text-ds-ink-3">{error}</p>
          <Button as={Link} to="/register" className="mt-5">
            {t("Crear mi cuenta de todas formas")}
          </Button>
        </div>
      ) : (
        <PulseLoader label={t("Verificando tu invitación…")} />
      )}
    </div>
  );
}
